import { useMutation } from "@tanstack/react-query";
import { service, useAuthConfig } from "@theprelude/core";
import { notifyError } from "@theprelude/ds";
import { JobStatusResponse } from "@theprelude/sdk";

const POLL_INTERVAL = 5000;

const save = (url: string, filename: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const onError = (error: Error) => {
  notifyError({
    message: "Error exporting endpoints",
    error,
    dismissible: true,
  });
};

const makeOnSuccess = (filename: string) => (jobStatus: JobStatusResponse) => {
  if (jobStatus.results?.url) {
    save(jobStatus.results.url, filename);
  } else if (jobStatus.error) {
    notifyError({
      message: "Error exporting endpoints",
      error: new Error(jobStatus.error),
      dismissible: true,
    });
  }
};

export const useDownloadJobFile = ({
  getJobId,
  filename,
}: {
  getJobId: () => Promise<string>;
  filename: string;
}) => {
  const authConfig = useAuthConfig();

  const onSuccess = makeOnSuccess(filename);

  const { mutate: download, isPending: isDownloading } = useMutation({
    mutationFn: async () => {
      const jobId = await getJobId();
      let result = await service(authConfig).jobs.getJobStatus(jobId);
      while (!result.end_time) {
        await sleep(POLL_INTERVAL);
        result = await service(authConfig).jobs.getJobStatus(jobId);
      }
      return result;
    },
    onSuccess,
    onError,
  });

  return { download, isDownloading };
};
